import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import { DEFAULT_TEST_CERTIFICATE_SETTINGS } from "../utils/reportIdentifiers";

const MONTH_LABELS = {
  "1": "January",
  "2": "February",
  "3": "March",
  "4": "April",
  "5": "May",
  "6": "June",
  "7": "July",
  "8": "August",
  "9": "September",
  "10": "October",
  "11": "November",
  "12": "December"
};

const MATERIAL_LABELS = {
  acb: "ACB",
  vcb: "VCB",
  mccb: "MCCB",
  other: "Other Equipment"
};

export default function TestCertificateSettings() {
  const [form, setForm] = useState(DEFAULT_TEST_CERTIFICATE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_BASE_URL + "/api/test-certificate-settings");
        const data = response.data || {};
        setForm({
          ...DEFAULT_TEST_CERTIFICATE_SETTINGS,
          ...data,
          monthCodes: {
            ...DEFAULT_TEST_CERTIFICATE_SETTINGS.monthCodes,
            ...(data.monthCodes || {})
          },
          materialCodes: {
            ...DEFAULT_TEST_CERTIFICATE_SETTINGS.materialCodes,
            ...(data.materialCodes || {})
          }
        });
      } catch (error) {
        setForm(DEFAULT_TEST_CERTIFICATE_SETTINGS);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const updateMonthCode = (monthKey, value) => {
    setForm((current) => ({
      ...current,
      monthCodes: {
        ...current.monthCodes,
        [monthKey]: value
      }
    }));
  };

  const updateMaterialCode = (materialKey, value) => {
    setForm((current) => ({
      ...current,
      materialCodes: {
        ...current.materialCodes,
        [materialKey]: value
      }
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await axios.put(API_BASE_URL + "/api/test-certificate-settings", {
        prefix: form.prefix,
        startingRunningNumber: form.startingRunningNumber,
        monthCodes: form.monthCodes,
        materialCodes: form.materialCodes
      });
      const data = response.data || {};
      setForm({
        ...DEFAULT_TEST_CERTIFICATE_SETTINGS,
        ...data,
        monthCodes: {
          ...DEFAULT_TEST_CERTIFICATE_SETTINGS.monthCodes,
          ...(data.monthCodes || {})
        },
        materialCodes: {
          ...DEFAULT_TEST_CERTIFICATE_SETTINGS.materialCodes,
          ...(data.materialCodes || {})
        }
      });
      alert("Test certificate settings updated successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save test certificate settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-[24px] border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading certificate settings...</div>;
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-700">Admin Settings</p>
        <h1 className="mt-2 text-3xl font-bold text-slate-950">Test Certificate Settings</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Configure the prefix, month codes, material codes, and starting running number used for dynamic GENMECH test certificate numbering.
        </p>
      </section>

      <section className="grid gap-4 xl:grid-cols-[320px_1fr]">
        <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
          <label className="mb-2 block text-sm font-semibold text-slate-700">Prefix</label>
          <input
            value={form.prefix}
            onChange={(event) => updateField("prefix", event.target.value)}
            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-sky-400"
          />

          <label className="mb-2 mt-4 block text-sm font-semibold text-slate-700">Starting Running Number</label>
          <input
            type="number"
            min="1"
            value={form.startingRunningNumber}
            onChange={(event) => updateField("startingRunningNumber", event.target.value)}
            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-sky-400"
          />

          <div className="mt-5 rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Preview</p>
            <p className="mt-2 font-mono text-sm font-bold text-slate-900">
              {(form.prefix || "GEN").trim() || "GEN"}/26/5/A/0001
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-bold text-slate-950">Month Codes</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {Object.entries(MONTH_LABELS).map(([monthKey, monthLabel]) => (
                <label key={monthKey} className="block rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <span className="mb-2 block text-sm font-semibold text-slate-700">{monthLabel}</span>
                  <input
                    value={form.monthCodes?.[monthKey] || ""}
                    onChange={(event) => updateMonthCode(monthKey, event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-sky-400"
                  />
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-bold text-slate-950">Material Codes</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {Object.entries(MATERIAL_LABELS).map(([materialKey, materialLabel]) => (
                <label key={materialKey} className="block rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <span className="mb-2 block text-sm font-semibold text-slate-700">{materialLabel}</span>
                  <input
                    value={form.materialCodes?.[materialKey] || ""}
                    onChange={(event) => updateMaterialCode(materialKey, event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-sky-400"
                  />
                </label>
              ))}
            </div>
          </section>
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={saveSettings}
          disabled={saving}
          className="rounded-2xl bg-slate-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
